import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PantryLocation } from '../utils/pantryLocation';
import {
  fetchPantryLearningFromCloud,
  savePantryLearningToCloud,
} from '../services/pantryLearningSync';

/**
 * What the user has taught the pantry matcher.
 *
 * The duplicate matcher is a pile of string rules, and string rules are always
 * wrong about *someone's* kitchen: "vin blanc" and "vin rouge" collapse to the
 * same key, while "tomates prunes" and "plum tomatoes" do not. Rather than
 * chase every case in code, the review inbox records the calls the user
 * actually makes and consults them first on the next run.
 *
 * Everything is keyed on `duplicateKeys()` output, not on display text, so a
 * lesson survives the row being renamed, re-cased or re-accented.
 *
 * Household-scoped: a correction made by one member helps the other.
 */
export interface KeyPair {
  a: string;
  b: string;
}

export interface PantryLearningState {
  /** Pairs the user confirmed are the same food. Transitive, via union-find. */
  aliases: KeyPair[];
  /** Pairs the user rejected. A hard blocklist, never suggested again. */
  distinct: KeyPair[];
  /**
   * Answers to "you have {a} — does that cover {b}?", keyed on canonical
   * ingredient ids. `a` is what is in the pantry, `b` is what the recipe wants,
   * so these are DIRECTED and must not be sorted the way `aliases` are.
   *
   * Deliberately separate from `aliases`. Limes covering lime juice is not the
   * same claim as limes and lime juice being one food, and filing it as an
   * alias would tell the duplicate matcher to merge the two pantry rows —
   * which deletes one of them.
   */
  covers: KeyPair[];
  /** The same question answered no. Stops it being asked again. */
  notCovers: KeyPair[];
  /** Where the user actually keeps this food, overriding the keyword rules. */
  zones: Record<string, PantryLocation>;
  /** What the user says this food is, overriding keyword detection. */
  categories: Record<string, string>;
  lastUpdated: string | null;
  loaded: boolean;
}

const initialState: PantryLearningState = {
  aliases: [],
  distinct: [],
  covers: [],
  notCovers: [],
  zones: {},
  categories: {},
  lastUpdated: null,
  loaded: false,
};

/** Order-independent identity for a pair, so (a,b) and (b,a) are one lesson. */
const pairId = (pair: KeyPair): string => [pair.a, pair.b].sort().join(' | ');

/** Direction-preserving identity, for the coverage answers. */
const directedId = (pair: KeyPair): string => `${pair.a} > ${pair.b}`;

const hasDirected = (list: KeyPair[], pair: KeyPair): boolean =>
  list.some(existing => directedId(existing) === directedId(pair));

const withoutDirected = (list: KeyPair[], pair: KeyPair): KeyPair[] =>
  list.filter(existing => directedId(existing) !== directedId(pair));

const hasPair = (list: KeyPair[], pair: KeyPair): boolean =>
  list.some(existing => pairId(existing) === pairId(pair));

const withoutPair = (list: KeyPair[], pair: KeyPair): KeyPair[] =>
  list.filter(existing => pairId(existing) !== pairId(pair));

export const loadPantryLearning = createAsyncThunk(
  'pantryLearning/load',
  async ({ userId, householdId }: { userId?: string; householdId?: string | null }) =>
    fetchPantryLearningFromCloud(userId, householdId)
);

/**
 * Persist after every lesson. The document is a handful of short strings, so a
 * write per correction costs nothing, and it means a lesson is never lost to a
 * reload — which would teach the user that correcting the app does not stick.
 */
export const savePantryLearning = createAsyncThunk(
  'pantryLearning/save',
  async (
    { userId, householdId }: { userId?: string; householdId?: string | null },
    { getState }
  ) => {
    const { pantryLearning } = getState() as { pantryLearning: PantryLearningState };
    await savePantryLearningToCloud(userId, householdId, pantryLearning);
  }
);

const pantryLearningSlice = createSlice({
  name: 'pantryLearning',
  initialState,
  reducers: {
    /** The user confirmed two rows are the same food. */
    learnAlias: (state, action: PayloadAction<KeyPair>) => {
      const pair = action.payload;
      if (!pair.a || !pair.b || pair.a === pair.b) return;
      if (hasPair(state.aliases, pair)) return;
      // A confirmation overrules an earlier rejection of the same pair.
      state.distinct = withoutPair(state.distinct, pair);
      state.aliases.push(pair);
      state.lastUpdated = new Date().toISOString();
    },

    /** The user said two rows are genuinely different foods. */
    learnDistinct: (state, action: PayloadAction<KeyPair>) => {
      const pair = action.payload;
      if (!pair.a || !pair.b || pair.a === pair.b) return;
      if (hasPair(state.distinct, pair)) return;
      state.aliases = withoutPair(state.aliases, pair);
      state.distinct.push(pair);
      state.lastUpdated = new Date().toISOString();
    },

    /** The user put this food somewhere. That beats any keyword rule. */
    learnZone: (state, action: PayloadAction<{ key: string; zone: PantryLocation }>) => {
      const { key, zone } = action.payload;
      if (!key) return;
      if (state.zones[key] === zone) return;
      state.zones[key] = zone;
      state.lastUpdated = new Date().toISOString();
    },

    /** The user told us what this food is. */
    learnCategory: (state, action: PayloadAction<{ key: string; category: string }>) => {
      const { key, category } = action.payload;
      if (!key || !category) return;
      if (state.categories[key] === category) return;
      state.categories[key] = category;
      state.lastUpdated = new Date().toISOString();
    },

    /**
     * The user answered "you have {a} — does that cover {b}?".
     *
     * `covers` is true for yes, false for no. Both are recorded: a no is as
     * much of an answer as a yes, and without it the same row asks again every
     * time the recipe is opened.
     */
    learnCoverage: (state, action: PayloadAction<KeyPair & { covers: boolean }>) => {
      const { a, b, covers } = action.payload;
      if (!a || !b || a === b) return;
      const pair = { a, b };
      const [into, outOf] = covers
        ? ([state.covers, 'notCovers'] as const)
        : ([state.notCovers, 'covers'] as const);
      state[outOf] = withoutDirected(state[outOf], pair);
      if (!hasDirected(into, pair)) into.push(pair);
      state.lastUpdated = new Date().toISOString();
    },

    /** Escape hatch: the lessons are the user's, so they can throw them away. */
    forgetPantryLearning: () => ({ ...initialState, loaded: true }),
  },
  extraReducers: (builder) => {
    builder.addCase(loadPantryLearning.fulfilled, (state, action) => {
      if (action.payload) {
        state.aliases = action.payload.aliases ?? [];
        state.distinct = action.payload.distinct ?? [];
        state.covers = action.payload.covers ?? [];
        state.notCovers = action.payload.notCovers ?? [];
        state.zones = action.payload.zones ?? {};
        state.categories = action.payload.categories ?? {};
        state.lastUpdated = action.payload.lastUpdated ?? null;
      }
      state.loaded = true;
    });
    builder.addCase(loadPantryLearning.rejected, (state) => {
      // Offline or unauthenticated: carry on with whatever is in memory rather
      // than blocking the pantry on a lookup that is only an optimisation.
      state.loaded = true;
    });
  },
});

export const {
  learnAlias,
  learnDistinct,
  learnZone,
  learnCategory,
  learnCoverage,
  forgetPantryLearning,
} = pantryLearningSlice.actions;

export default pantryLearningSlice.reducer;
